import { cx } from "@ui/utils/cx";
import { useEffect, useId, useRef, useState } from "react";

// Muted "stamp ink" — a desaturated slate-indigo that reads as an official passport
// mark, never the alarm-orange the rarity used to be. One source of truth so the
// rings, curved copy, and centre figure all ink the same.
const INK = "#586079";

// Geometry on a 72×72 canvas, centred at (36,36). Two fine dotted rings form the
// stamp border; the curved copy rides arcs just inside them, with the figure in the
// middle. Coordinates are tuned against the rendered stamp — re-measure if resized.
const CENTER = 36;
// Rings. The copy rides arcs CONCENTRIC with these (same centre) so its curve runs
// exactly parallel to the dotted rim.
const RING_OUTER = 34.5;
const RING_DOTTED = 31;
// Text arcs, both centred on (36,36). The top label's glyphs grow outward from its
// baseline, the bottom label's grow inward, so the two baselines sit at different
// radii (24 / 29) but the LETTERS occupy the same band (r 24–29) — an even ~2px gap
// inside the dotted ring, top and bottom matching.
const TOP_ARC = `M ${CENTER - 24} 36 A 24 24 0 0 1 ${CENTER + 24} 36`;
const BOTTOM_ARC = `M ${CENTER - 29} 36 A 29 29 0 0 0 ${CENTER + 29} 36`;
const TOP_ARC_MAX = 62; // px budget before the top label shrinks to fit
const BOTTOM_ARC_MAX = 84; // the lower arc is longer, so it holds more
const ARC_FONT = 7; // cap height ≈ 5, filling the 5px band between value and rim
const VALUE_FONT = 21;
const VALUE_MAX_WIDTH = 34; // "<1%" is the widest figure — shrink rather than overflow

/**
 * Shrink-to-fit a label to a width budget. Measured from a hidden copy at the base
 * size (re-measured once webfonts settle, since glyph widths differ from the
 * fallback face). Mirrors the approach in CurvedSectionTitle.
 */
function useFitFont(label: string, maxWidth: number, baseFont: number) {
  const ref = useRef<SVGTextElement>(null);
  const [fontSize, setFontSize] = useState(baseFont);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled || !node.isConnected) return;
      node.textContent = label;
      const width = node.getComputedTextLength();
      setFontSize(width > maxWidth ? (baseFont * maxWidth) / width : baseFont);
    };
    measure();
    if (document.fonts?.ready) void document.fonts.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, [label, maxWidth, baseFont]);
  return [ref, fontSize] as const;
}

interface RarityStampProps {
  /** The rarity figure already formatted for display, e.g. "3%" or "<1%". */
  value: string;
  /** Curved copy over the top of the stamp, e.g. "Held by". */
  topLabel: string;
  /** Curved copy under the stamp, e.g. "of travellers". */
  bottomLabel: string;
  className?: string;
}

/**
 * A passport-style rubber stamp for a badge's rarity: the share of travellers who
 * hold it set as an inked entry mark — the figure in the middle, the localized
 * "held by … of travellers" copy curved around the ring. Slightly askew so it reads
 * as stamped onto the card by hand. Decorative (aria-hidden); the rarity is exposed
 * to assistive tech by the visually-hidden text the BadgeHoverCard renders.
 */
export function RarityStamp({ value, topLabel, bottomLabel, className }: RarityStampProps) {
  const topId = useId();
  const bottomId = useId();
  // Uppercase up front so the shrink-to-fit measures the SAME glyphs that render —
  // measuring the mixed-case form under-shrinks the wider uppercased copy and lets
  // long localized labels overrun the ring.
  const top = topLabel.toUpperCase();
  const bottom = bottomLabel.toUpperCase();
  const [topRef, topFont] = useFitFont(top, TOP_ARC_MAX, ARC_FONT);
  const [bottomRef, bottomFont] = useFitFont(bottom, BOTTOM_ARC_MAX, ARC_FONT);
  const [valueRef, valueFont] = useFitFont(value, VALUE_MAX_WIDTH, VALUE_FONT);

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 72 72"
      className={cx("-rotate-[7deg] select-none", className)}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <path id={topId} d={TOP_ARC} fill="none" />
        <path id={bottomId} d={BOTTOM_ARC} fill="none" />
      </defs>

      {/* Double border — a solid rim with a dotted ring just inside it. */}
      <circle cx={CENTER} cy={CENTER} r={RING_OUTER} fill="none" stroke={INK} strokeWidth="1.4" />
      <circle
        cx={CENTER}
        cy={CENTER}
        r={RING_DOTTED}
        fill="none"
        stroke={INK}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeDasharray="0.5 3.1"
      />

      {/* Hidden measurers for the shrink-to-fit copy. */}
      <text ref={topRef} fontSize={ARC_FONT} letterSpacing="1.2" visibility="hidden" />
      <text ref={bottomRef} fontSize={ARC_FONT} letterSpacing="1.2" visibility="hidden" />
      <text ref={valueRef} fontSize={VALUE_FONT} fontWeight={700} visibility="hidden" />

      <text fill={INK} fontSize={topFont} fontWeight={600} letterSpacing="1.2" textAnchor="middle">
        <textPath href={`#${topId}`} startOffset="50%">
          {top}
        </textPath>
      </text>
      <text fill={INK} fontSize={bottomFont} fontWeight={600} letterSpacing="1.2" textAnchor="middle">
        <textPath href={`#${bottomId}`} startOffset="50%">
          {bottom}
        </textPath>
      </text>

      {/* Centre figure — the rarity itself. */}
      <text
        x={CENTER}
        y={CENTER + valueFont * 0.34}
        fill={INK}
        fontSize={valueFont}
        fontWeight={700}
        textAnchor="middle"
      >
        {value}
      </text>
    </svg>
  );
}
